import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="39406" className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-[#1A2A4E]/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium tracking-tight text-[#1A2A4E]/80 transition-colors hover:text-[#1A2A4E]">
                {item.label}
                <span className="absolute -bottom-1 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-[#D4AF37] transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8 items-center">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 bg-[#1A2A4E]/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center gap-8 p-8 w-full">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-light text-[#1A2A4E]/90 transition-colors hover:text-[#008080]" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <Button asChild className="mt-8 w-full max-w-xs bg-[#1A2A4E] text-white hover:bg-[#1A2A4E]/90 rounded-sm h-12 text-lg">
        <Link to="/LetsTalkPage" onClick={onClose}>
          Schedule Consultation
        </Link>
      </Button>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={cn("fixed top-0 left-0 z-50 w-full transition-all duration-500 ease-in-out border-b border-transparent", isScrolled ? "bg-white/80 backdrop-blur-md border-b-[#D4AF37]/20 py-3 shadow-md" : "bg-white py-5")}>
      <div className="container mx-auto flex items-center justify-between px-6 md:px-12">
        {}
        <div className="flex-shrink-0 z-50">
          <Link to="/HomePage" className="flex items-center">
            <img src="https://climbing-leech.10web.cloud/wp-content/uploads/2026/03/regenerate_logo_white.png" alt="Cyril Consulting Group Logo" className="h-7 w-auto object-contain brightness-0 contrast-100" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center px-4">
          <MenuProvider menu_id="53">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0 items-center gap-4">
          <Button asChild className="bg-[#1A2A4E] text-white hover:bg-[#1A2A4E]/90 font-medium px-6 rounded-sm transition-transform hover:scale-105 shadow-[0_4px_12px_rgba(26,42,78,0.15)]">
            <Link to="/LetsTalkPage">Schedule Consultation</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="z-50 md:hidden text-[#1A2A4E] hover:bg-[#1A2A4E]/10 hover:text-[#1A2A4E]" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>

        {}
        <div className={cn("fixed inset-0 z-40 flex flex-col items-center justify-center bg-white transition-all duration-500 md:hidden", isMobileMenuOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4 pointer-events-none")}>
          <div className="w-full max-w-md px-6">
            <MenuProvider menu_id="53">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>
      </div>
    </header>;
}